import { useState } from 'react'
import { useSelector } from 'react-redux'
import { EyeIcon, WarningCircleIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopDialog,
    ScoopText,
    ScoopSpinner,
    ScoopCenter,
    ScoopColumn,
    ScoopRow,
    ScoopAccordion,
} from '../../../../../../ui'
import { usePortalPreview } from '../../../../../../../api/hooks/domainIntelligence/usePortalPreview'
import { SeverityTag } from '../../../SeverityTag/SeverityTag'
import styles from './PortalPreview.module.scss'

/**
 * Operator preview of the CUSTOMER-FACING report for a group run.
 *
 * Fetches the exact allowlisted JSON the Portal receives (PortalReportAssembler
 * via getDomainIntelligencePortalReport) so an operator can check what the
 * customer will see BEFORE publication. This is a CONTENT preview — the Portal
 * app renders its own layout — so the raw contract JSON is always available
 * below the structured view.
 */
export const PortalPreview = ({ runPath, entitiesNoun = 'Entities' }) => {
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const [open, setOpen] = useState(false)

    const { portalReport, loadingPortalReport, portalReportError } = usePortalPreview(
        workspaceID,
        runPath,
        open
    )

    const summary = portalReport?.summary
    const entities = Array.isArray(portalReport?.entities) ? portalReport.entities : []
    const distribution = Array.isArray(portalReport?.statusDistribution?.buckets)
        ? portalReport.statusDistribution.buckets
        : Array.isArray(portalReport?.statusDistribution)
          ? portalReport.statusDistribution
          : []

    return (
        <>
            <ScoopButton
                variant="grey"
                size="sm"
                onClick={() => setOpen(true)}
                disabled={!runPath}
            >
                <EyeIcon size={16} />
                Customer preview
            </ScoopButton>

            <ScoopDialog
                open={open}
                onClose={() => setOpen(false)}
                title="What the customer will see"
                maxWidth="md"
            >
                <ScoopColumn className={styles.container}>
                    <ScoopText className={styles.disclaimer}>
                        This is the exact content the customer web Portal receives for this
                        run — reviewed here before publication. The Portal renders its own
                        layout; wording, labels, and inclusion/exclusion are what matter in
                        this view.
                    </ScoopText>

                    {loadingPortalReport && (
                        <ScoopCenter className={styles.state}>
                            <ScoopSpinner size={28} />
                        </ScoopCenter>
                    )}

                    {portalReportError && (
                        <div className={styles.errorRow}>
                            <WarningCircleIcon size={16} className={styles.errorIcon} />
                            <ScoopText className={styles.error}>
                                Could not assemble the customer report: {portalReportError.message}
                            </ScoopText>
                        </div>
                    )}

                    {portalReport && (
                        <>
                            {/* One metadata line — text, not a chip row (D2);
                                schemaVersion demoted to the footer (D8). */}
                            <ScoopRow className={styles.metaRow}>
                                <ScoopText className={styles.metaLine}>
                                    {[
                                        portalReport.customer?.label,
                                        portalReport.scope?.displayLabel,
                                        portalReport.cycle?.dataThrough
                                            ? `Data through ${portalReport.cycle.dataThrough}`
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </ScoopText>
                            </ScoopRow>

                            {summary?.headline && (
                                <div className={styles.section}>
                                    <ScoopText className={styles.headline}>
                                        {summary.headline}
                                    </ScoopText>
                                    {summary.narrative && (
                                        <ScoopText className={styles.narrative}>
                                            {summary.narrative}
                                        </ScoopText>
                                    )}
                                </div>
                            )}

                            {distribution.length > 0 && (
                                <ScoopRow className={styles.distribution}>
                                    {distribution.map((bucket, idx) => (
                                        <span key={idx} className={styles.distributionBucket}>
                                            <SeverityTag
                                                severity={
                                                    bucket.severity || bucket.key || bucket.label
                                                }
                                            />
                                            <ScoopText className={styles.metaText}>
                                                × {bucket.count ?? '—'}
                                            </ScoopText>
                                        </span>
                                    ))}
                                </ScoopRow>
                            )}

                            {entities.length > 0 && (
                                <div className={styles.section}>
                                    <ScoopText className={styles.sectionTitle}>
                                        {entitiesNoun} as the customer sees them ({entities.length})
                                    </ScoopText>
                                    {entities.map((entity, idx) => (
                                        <div
                                            key={entity.entityId || idx}
                                            className={styles.entityRow}
                                        >
                                            <ScoopRow className={styles.entityHeader}>
                                                <ScoopText className={styles.entityName}>
                                                    {entity.displayName ||
                                                        entity.displayLabel ||
                                                        entity.displayId ||
                                                        entity.entityId}
                                                </ScoopText>
                                                {entity.severity && (
                                                    <SeverityTag severity={entity.severity} />
                                                )}
                                            </ScoopRow>
                                            {(entity.headline || entity.narrative) && (
                                                <ScoopText className={styles.entityNarrative}>
                                                    {entity.headline || entity.narrative}
                                                </ScoopText>
                                            )}
                                            {!entity.headline && !entity.narrative && (
                                                <ScoopText className={styles.entityNarrativeEmpty}>
                                                    No narrative for this entity
                                                </ScoopText>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <ScoopAccordion>
                                <ScoopAccordion.Summary>
                                    <ScoopText className={styles.rawJsonTitle}>
                                        Developer: contract JSON (exactly what the Portal
                                        receives)
                                    </ScoopText>
                                </ScoopAccordion.Summary>
                                <ScoopAccordion.Details>
                                    <pre className={styles.rawJson}>
                                        {JSON.stringify(portalReport, null, 2)}
                                    </pre>
                                </ScoopAccordion.Details>
                            </ScoopAccordion>

                            {portalReport.schemaVersion && (
                                <ScoopText className={styles.footerCaption}>
                                    Contract {portalReport.schemaVersion}
                                </ScoopText>
                            )}
                        </>
                    )}
                </ScoopColumn>
            </ScoopDialog>
        </>
    )
}
